import React, { useState, useEffect, useMemo } from "react";
import { authenticatedFetch } from "@/libs/api";
import {
  Card,
  Table,
  Tag,
  Space,
  Button,
  Typography,
  Row,
  Col,
  Statistic,
  Checkbox,
  Divider,
  message,
  Dropdown,
  Spin,
  theme,
} from "antd";
import {
  DollarOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  DownOutlined,
  CheckOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import PageLayout from "@components/pageLayout/PageLayout.react";

const { Title, Text } = Typography;

// Sharp, tech-style input
const inputStyle = { borderRadius: 2 };

export default function OutstandingPayable() {
  const { token } = theme.useToken();
  const [loading, setLoading] = useState(true);

  // Partner payables state
  const [partnerPayables, setPartnerPayables] = useState([]);

  // Client/End-user payables state
  const [clientPayables, setClientPayables] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch company ledger for partner payables and customer ledger for client payables
        const [compRes, custRes] = await Promise.all([
          authenticatedFetch("/api/settlements/ledger/company?limit=100"),
          authenticatedFetch("/api/settlements/ledger/customer?limit=100"),
        ]);

        if (compRes.ok) {
          const compJson = await compRes.json();
          if (compJson.r === 0 && compJson.data) {
            // Only show entries with outstanding balances (closing_balance > 0)
            const entries = compJson.data
              .filter((e) => Number(e.closing_balance) > 0)
              .map((e, i) => ({
                id: `PAY-P${String(i + 1).padStart(3, "0")}`,
                partnerId: e.company_id || "",
                partnerName: e.company_id || "Partner",
                periodStart: e.date,
                periodEnd: e.date,
                systemCount: 0,
                totalKwh: 0,
                calculatedAmount: Number(e.closing_balance) || 0,
                checked: true,
              }));
            setPartnerPayables(entries);
          }
        }

        if (custRes.ok) {
          const custJson = await custRes.json();
          if (custJson.r === 0 && custJson.data) {
            // Only show entries with outstanding balances (closing_balance > 0)
            const entries = custJson.data
              .filter((e) => Number(e.closing_balance) > 0)
              .map((e, i) => ({
                id: `PAY-C${String(i + 1).padStart(3, "0")}`,
                clientId: e.customer_id || "",
                clientName: e.customer_id || "Client",
                email: "",
                systemSn: "",
                periodStart: e.date,
                periodEnd: e.date,
                totalKwh: 0,
                calculatedAmount: Number(e.closing_balance) || 0,
                checked: true,
              }));
            setClientPayables(entries);
          }
        }
      } catch (error) {
        console.error("Failed to fetch outstanding payable:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Calculate partner stats
  const partnerStats = useMemo(() => {
    const total = partnerPayables.reduce((sum, p) => sum + p.calculatedAmount, 0);
    const planned = partnerPayables
      .filter((p) => p.checked)
      .reduce((sum, p) => sum + p.calculatedAmount, 0);
    const unchecked = total - planned;
    return { total, planned, unchecked };
  }, [partnerPayables]);

  // Calculate client stats
  const clientStats = useMemo(() => {
    const total = clientPayables.reduce((sum, c) => sum + c.calculatedAmount, 0);
    const planned = clientPayables
      .filter((c) => c.checked)
      .reduce((sum, c) => sum + c.calculatedAmount, 0);
    const unchecked = total - planned;
    return { total, planned, unchecked };
  }, [clientPayables]);

  // Combined stats
  const totalOutstanding = partnerStats.total + clientStats.total;
  const totalPlanned = partnerStats.planned + clientStats.planned;

  const handlePartnerCheck = (id, checked) => {
    setPartnerPayables((prev) =>
      prev.map((p) => (p.id === id ? { ...p, checked } : p))
    );
  };

  const handleClientCheck = (id, checked) => {
    setClientPayables((prev) =>
      prev.map((c) => (c.id === id ? { ...c, checked } : c))
    );
  };

  const handleSelectAllPartners = (checked) => {
    setPartnerPayables((prev) => prev.map((p) => ({ ...p, checked })));
  };

  const handleSelectAllClients = (checked) => {
    setClientPayables((prev) => prev.map((c) => ({ ...c, checked })));
  };

  const handleDownloadPaymentFile = () => {
    const checkedPartners = partnerPayables.filter((p) => p.checked);
    const checkedClients = clientPayables.filter((c) => c.checked);
    message.success(
      `Downloading payment file: ${checkedPartners.length} partner invoices, ${checkedClients.length} client payments`
    );
  };

  const handleApprovePayments = () => {
    const checkedClients = clientPayables.filter((c) => c.checked);
    message.success(`Approved ${checkedClients.length} end user payments`);
  };

  const handleGenerateRCTI = () => {
    const checkedPartners = partnerPayables.filter((p) => p.checked);
    message.success(`Generated RCTI for ${checkedPartners.length} partner invoices`);
  };

  const exportMenuItems = [
    { key: "excel", label: "Export to Excel", icon: <FileExcelOutlined /> },
    { key: "csv", label: "Export to CSV", icon: <FileExcelOutlined /> },
    { key: "pdf", label: "Export to PDF", icon: <FilePdfOutlined /> },
  ];

  const handleExport = ({ key }) => {
    message.success(`Exporting to ${key.toUpperCase()}...`);
  };

  const partnerColumns = [
    {
      title: (
        <Checkbox
          checked={partnerPayables.every((p) => p.checked)}
          indeterminate={
            partnerPayables.some((p) => p.checked) &&
            !partnerPayables.every((p) => p.checked)
          }
          onChange={(e) => handleSelectAllPartners(e.target.checked)}
        />
      ),
      dataIndex: "checked",
      key: "checked",
      width: 50,
      render: (checked, record) => (
        <Checkbox
          checked={checked}
          onChange={(e) => handlePartnerCheck(record.id, e.target.checked)}
        />
      ),
    },
    {
      title: "Partner",
      dataIndex: "partnerName",
      key: "partnerName",
      render: (name, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.partnerId}</Text>
        </Space>
      ),
    },
    {
      title: "Period",
      key: "period",
      render: (_, record) => (
        <Text style={{ fontSize: 12 }}>{record.periodStart} ~ {record.periodEnd}</Text>
      ),
    },
    {
      title: "Systems",
      dataIndex: "systemCount",
      key: "systemCount",
      align: "right",
    },
    {
      title: "kWh",
      dataIndex: "totalKwh",
      key: "totalKwh",
      align: "right",
      render: (kwh) => kwh.toLocaleString(),
    },
    {
      title: "Amount",
      dataIndex: "calculatedAmount",
      key: "calculatedAmount",
      align: "right",
      render: (amount, record) => (
        <Text
          strong
          style={{ color: record.checked ? token.colorPrimary : token.colorTextDisabled }}
        >
          ${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </Text>
      ),
    },
  ];

  const clientColumns = [
    {
      title: (
        <Checkbox
          checked={clientPayables.every((c) => c.checked)}
          indeterminate={
            clientPayables.some((c) => c.checked) &&
            !clientPayables.every((c) => c.checked)
          }
          onChange={(e) => handleSelectAllClients(e.target.checked)}
        />
      ),
      dataIndex: "checked",
      key: "checked",
      width: 50,
      render: (checked, record) => (
        <Checkbox
          checked={checked}
          onChange={(e) => handleClientCheck(record.id, e.target.checked)}
        />
      ),
    },
    {
      title: "Client",
      dataIndex: "clientName",
      key: "clientName",
      render: (name, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.email}</Text>
        </Space>
      ),
    },
    {
      title: "System",
      dataIndex: "systemSn",
      key: "systemSn",
      render: (sn) => <Text code style={{ fontSize: 11 }}>{sn}</Text>,
    },
    {
      title: "Period",
      key: "period",
      render: (_, record) => (
        <Text style={{ fontSize: 12 }}>{record.periodStart} ~ {record.periodEnd}</Text>
      ),
    },
    {
      title: "kWh",
      dataIndex: "totalKwh",
      key: "totalKwh",
      align: "right",
    },
    {
      title: "Amount",
      dataIndex: "calculatedAmount",
      key: "calculatedAmount",
      align: "right",
      render: (amount, record) => (
        <Text
          strong
          style={{ color: record.checked ? token.colorPrimary : token.colorTextDisabled }}
        >
          ${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </Text>
      ),
    },
  ];

  return (
    <PageLayout active="outstandingPayable">
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <Title level={2} style={{ marginBottom: 4 }}>
              Outstanding Payable
            </Title>
            <Text type="secondary" style={{ fontSize: 16 }}>
              Manage payments to partners and end users
            </Text>
          </div>
          <Dropdown menu={{ items: exportMenuItems, onClick: handleExport }}>
            <Button
              size="large"
              style={{
                borderRadius: 8,
                border: "1px solid #e0e0e0",
                background: "#fff",
                fontWeight: 500,
              }}
            >
              Export <DownOutlined style={{ fontSize: 10 }} />
            </Button>
          </Dropdown>
        </div>
      </div>

      {/* Summary Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Total Outstanding"
              value={totalOutstanding}
              precision={2}
              prefix="$"
              valueStyle={{ color: token.colorPrimary }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Planned Payment"
              value={totalPlanned}
              precision={2}
              prefix="$"
              valueStyle={{ color: token.colorSuccess }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Partner Payable"
              value={partnerStats.total}
              precision={2}
              prefix="$"
              valueStyle={{ color: "#f5a623" }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Client Payable"
              value={clientStats.total}
              precision={2}
              prefix="$"
              valueStyle={{ color: "#13c2c2" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Partner Section */}
      <Card
        bordered={false}
        style={{ borderRadius: 12, marginBottom: 24 }}
        title={
          <Space>
            <TeamOutlined style={{ color: "#f5a623" }} />
            <Text strong>Partner RCTI Invoices</Text>
          </Space>
        }
        extra={
          <Button
            type="link"
            icon={<CheckOutlined />}
            onClick={handleGenerateRCTI}
            style={{ color: "#f5a623", fontWeight: 500, padding: 0 }}
          >
            Generate RCTI
          </Button>
        }
      >
        {/* Partner Stats */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Statistic
              title="Total"
              value={partnerStats.total}
              precision={2}
              prefix="$"
              valueStyle={{ fontSize: 16 }}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="Planned"
              value={partnerStats.planned}
              precision={2}
              prefix="$"
              valueStyle={{ fontSize: 16, color: token.colorSuccess }}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="Excluded"
              value={partnerStats.unchecked}
              precision={2}
              prefix="$"
              valueStyle={{ fontSize: 16, color: token.colorTextDisabled }}
            />
          </Col>
        </Row>
        <Divider style={{ margin: "12px 0" }} />
        <Table
          columns={partnerColumns}
          dataSource={partnerPayables}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 5 }}
          size="small"
          rowClassName={(record) => (!record.checked ? "row-unchecked" : "")}
        />
      </Card>

      {/* Client Section */}
      <Card
        bordered={false}
        style={{ borderRadius: 12 }}
        title={
          <Space>
            <UserOutlined style={{ color: "#13c2c2" }} />
            <Text strong>End User Payments</Text>
          </Space>
        }
        extra={
          <Button
            type="link"
            icon={<CheckOutlined />}
            onClick={handleApprovePayments}
            style={{ color: "#13c2c2", fontWeight: 500, padding: 0 }}
          >
            Approve Payments
          </Button>
        }
      >
        {/* Client Stats */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Statistic
              title="Total"
              value={clientStats.total}
              precision={2}
              prefix="$"
              valueStyle={{ fontSize: 16 }}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="Planned"
              value={clientStats.planned}
              precision={2}
              prefix="$"
              valueStyle={{ fontSize: 16, color: token.colorSuccess }}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="Excluded"
              value={clientStats.unchecked}
              precision={2}
              prefix="$"
              valueStyle={{ fontSize: 16, color: token.colorTextDisabled }}
            />
          </Col>
        </Row>
        <Divider style={{ margin: "12px 0" }} />
        <Table
          columns={clientColumns}
          dataSource={clientPayables}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 5 }}
          size="small"
          rowClassName={(record) => (!record.checked ? "row-unchecked" : "")}
        />
      </Card>

      <style>{`
        .row-unchecked {
          opacity: 0.5;
          background-color: #fafafa;
        }
      `}</style>
    </PageLayout>
  );
}
